import React, { useState, useEffect } from "react";
import { LayoutElement, LayoutElementType } from "./layoutTypes";
import { AVAILABLE_ELEMENT_TYPES } from "../index";
import { useRightSidebarConfig } from "../side-bars/right-context-sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/InputDefault";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/DefaultSelectDropdown";
import { Switch } from "@/components/ui/switch";
import { DATA_SOURCES, DataSourceType } from "@/lib/dataSources";
import DataSourceOrganizer from "@/components/ui/DataSourceOrganizer";

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
interface ElementSettingsSidebarProps {
  element: LayoutElement;
  onTypeChange: (elementId: string, newType: LayoutElementType) => void;
  onPropsChange: (elementId: string, newProps: Record<string, unknown>) => void;
  onRemove: (elementId: string) => void;
  availableTypes: LayoutElementType[];
}

export default function ElementSettingsSidebar({
  element,
  onTypeChange,
  onPropsChange,
  onRemove,
  availableTypes,
}: ElementSettingsSidebarProps) {
  const { t } = usePlatformTranslation();
  const [isDirty, setIsDirty] = useState(false);
  const [localProps, setLocalProps] = useState(element.props || {});
  const [localType, setLocalType] = useState(element.type);
  const { close } = useRightSidebarConfig();

  // Sync local state with element prop when it changes
  useEffect(() => {
    setLocalProps(element.props || {});
    setLocalType(element.type);
    setIsDirty(false);
  }, [element.id, element.type, element.props]);

  const handlePropsChange = (key: string, value: unknown) => {

    const newProps = { ...localProps, [key]: value };

    // Clean up irrelevant properties for the current widget type
    if (
      localType === "stats-widget" ||
      localType === "title-widget"
    ) {
      delete newProps.columnLabels;
      delete newProps.selectedColumns;
      delete newProps.dataSource; // For title-widget, stats-widget only uses dataSource optionally
    }
    if (localType === "stats-widget") {
      // stats-widget only uses label, value, trend, dataSource
      Object.keys(newProps).forEach((prop) => {
        if (!['label', 'value', 'trend', 'dataSource', 'shadowType', 'shadowColor', 'backgroundColor'].includes(prop)) {
          delete newProps[prop];
        }
      });
    }
    if (localType === "title-widget") {
      // title-widget only uses title, subtitle, size
      Object.keys(newProps).forEach((prop) => {
        if (!['title', 'subtitle', 'size', 'shadowType', 'shadowColor', 'backgroundColor'].includes(prop)) {
          delete newProps[prop];
        }
      });
    }

    setLocalProps(newProps);
    setIsDirty(true);
  };

  const handleSave = () => {
    // Apply changes only when user clicks save
    onPropsChange(element.id, localProps);
    if (localType !== element.type) {
      onTypeChange(element.id, localType);
    }
    setIsDirty(false);
  };

  const handleReset = () => {
    const originalProps = element.props || {};
    setLocalProps(originalProps);
    setLocalType(element.type);
    setIsDirty(false);
  };

  const handleTypeChange = (newType: LayoutElementType) => {
    setLocalType(newType);
    setIsDirty(true);
  };

  const handleRemove = () => {
    onRemove(element.id);
    close(); // Close the sidebar after removing the element
  };

  const renderCommonProps = () => (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="shadowType">{t('platform.layout.element-layout.shadow_style')}</Label>
        <Select
          value={(localProps.shadowType as string) || "black"}
          onValueChange={(value) => handlePropsChange("shadowType", value)}
        >
          <SelectTrigger>
            <SelectValue placeholder={t('platform.layout.element-layout.select_shadow_style')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="black">{t('platform.layout.element-layout.black')}</SelectItem>
            <SelectItem value="gradient">{t('platform.layout.element-layout.gradient')}</SelectItem>
            <SelectItem value="none">{t('platform.layout.element-layout.none')}</SelectItem>
            <SelectItem value="color">{t('platform.layout.element-layout.custom_color')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {(localProps.shadowType as string) === "color" && (
        <div className="space-y-2">
          <Label htmlFor="shadowColor">{t('platform.layout.element-layout.shadow_color')}</Label>
          <div className="flex items-center space-x-2">
            <Input
              id="shadowColor"
              type="color"
              value={(localProps.shadowColor as string) || "#000000"}
              onChange={(e) => handlePropsChange("shadowColor", e.target.value)}
              className="w-16 h-10 p-1 border rounded"
            />
            <Input
              type="text"
              value={(localProps.shadowColor as string) || "#000000"}
              onChange={(e) => handlePropsChange("shadowColor", e.target.value)}
              className="flex-1"
              placeholder="#000000"
            />
          </div>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="backgroundColor">{t('platform.layout.element-layout.background_color')}</Label>
        <div className="flex items-center space-x-2">
          <Input
            id="backgroundColor"
            type="color"
            value={(localProps.backgroundColor as string) || "#ffffff"}
            onChange={(e) =>
              handlePropsChange("backgroundColor", e.target.value)
            }
            className="w-16 h-10 p-1 border rounded"
          />
          <Input
            type="text"
            value={(localProps.backgroundColor as string) || "#ffffff"}
            onChange={(e) =>
              handlePropsChange("backgroundColor", e.target.value)
            }
            className="flex-1"
            placeholder="#ffffff"
          />
        </div>
      </div>
    </div>
  );

  const renderTitleWidgetProps = () => (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="title">{t('platform.layout.element-layout.title_text')}</Label>
        <Input
          id="title"
          value={(localProps.title as string) || "Title"}
          onChange={(e) => handlePropsChange("title", e.target.value)}
          placeholder={t('platform.layout.element-layout.enter_title_text')}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="subtitle">{t('platform.layout.element-layout.subtitle')}</Label>
        <Input
          id="subtitle"
          value={(localProps.subtitle as string) || ""}
          onChange={(e) => handlePropsChange("subtitle", e.target.value)}
          placeholder={t('platform.layout.element-layout.enter_subtitle')}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="titleSize">{t('platform.common.size')}</Label>
        <Select
          value={(localProps.size as string) || "medium"}
          onValueChange={(value) => handlePropsChange("size", value)}
        >
          <SelectTrigger>
            <SelectValue placeholder={t('platform.layout.element-layout.select_size')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="small">{t('platform.common.size.small')}</SelectItem>
            <SelectItem value="medium">{t('platform.common.size.medium')}</SelectItem>
            <SelectItem value="large">{t('platform.common.size.large')}</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );

  const renderStatsWidgetProps = () => (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="label">{t('platform.common.label')}</Label>
        <Input
          id="label"
          value={(localProps.label as string) || ""}
          onChange={(e) => handlePropsChange("label", e.target.value)}
          placeholder={t('platform.layout.element-layout.enter_label')}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="dataSource">{t('platform.layout.element-layout.data_source')}</Label>
        <Select
          value={(localProps.dataSource as string) || "manual"}
          onValueChange={(value) => {
            console.log('Select onValueChange called with value:', value);
            handlePropsChange(
              "dataSource",
              value === "manual" ? "" : (value as DataSourceType)
            );
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder={t('platform.layout.element-layout.select_data_source')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="manual">Manual Value</SelectItem>
            {Object.values(DATA_SOURCES).map((source) => (
              <SelectItem key={source.type} value={source.type}>
                {source.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground">
          {localProps.dataSource
            ? `Will show count of ${DATA_SOURCES[
                localProps.dataSource as DataSourceType
              ]?.title.toLowerCase()}`
            : "Enter a manual value below"}
        </span>
      </div>

      {!localProps.dataSource && (
        <div className="space-y-2">
          <Label htmlFor="value">{t('platform.layout.element-layout.manual_value_label')}</Label>
          <Input
            id="value"
            value={(localProps.value as string) || ""}
            onChange={(e) => handlePropsChange("value", e.target.value)}
            placeholder={t('platform.layout.element-layout.enter_value')}
          />
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="trend">{t('platform.layout.element-layout.trend')}</Label>
        <Input
          id="trend"
          value={(localProps.trend as string) || ""}
          onChange={(e) => handlePropsChange("trend", e.target.value)}
          placeholder={t('platform.layout.element-layout.enter_trend_eg_52')}
        />
      </div>
    </div>
  );

  const renderListWidgetProps = () => {
    const currentDataSource = localProps.dataSource as DataSourceType;
    const selectedColumns = Array.isArray(localProps.selectedColumns)
      ? (localProps.selectedColumns as string[])
      : [];
    const columnLabels =
      (localProps.columnLabels as Record<string, string>) || {};

    return (
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="listTitle">Title</Label>
          <Input
            id="listTitle"
            value={(localProps.title as string) || "List"}
            onChange={(e) => handlePropsChange("title", e.target.value)}
            placeholder={t('platform.layout.element-layout.enter_list_title')}
          />
        </div>

        <DataSourceOrganizer
          dataSource={currentDataSource}
          selectedColumns={selectedColumns}
          columnLabels={columnLabels}
          onDataSourceChange={(dataSource) =>
            handlePropsChange("dataSource", dataSource)
          }
          onSelectedColumnsChange={(columns) =>
            handlePropsChange("selectedColumns", columns)
          }
          onColumnLabelsChange={(labels) =>
            handlePropsChange("columnLabels", labels)
          }
        />

        <div className="space-y-2">
          <Label htmlFor="nrOfItems">{t('platform.layout.element-layout.number_of_items')}</Label>
          <Input
            id="nrOfItems"
            value={(localProps.nrOfItems as string) || "5"}
            onChange={(e) => handlePropsChange("nrOfItems", e.target.value)}
            placeholder={t('platform.layout.element-layout.enter_number_of_items')}
          />
        </div>
      </div>
    );
  };

  const renderExpandableTableWidgetProps = () => {
    const currentDataSource = localProps.dataSource as DataSourceType;
    const selectedColumns = Array.isArray(localProps.selectedColumns)
      ? (localProps.selectedColumns as string[])
      : [];
    const columnLabels =
      (localProps.columnLabels as Record<string, string>) || {};

    return (
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="expandableTableTitle">Title</Label>
          <Input
            id="expandableTableTitle"
            value={(localProps.title as string) || "Expandable Table"}
            onChange={(e) => handlePropsChange("title", e.target.value)}
            placeholder="Enter table title"
          />
        </div>

        <DataSourceOrganizer
          dataSource={currentDataSource}
          selectedColumns={selectedColumns}
          columnLabels={columnLabels}
          onDataSourceChange={(dataSource) =>
            handlePropsChange("dataSource", dataSource)
          }
          onSelectedColumnsChange={(columns) =>
            handlePropsChange("selectedColumns", columns)
          }
          onColumnLabelsChange={(labels) =>
            handlePropsChange("columnLabels", labels)
          }
        />

        {/* Expandable Content Settings */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium">{t('platform.layout.element-layout.expandable_content_settings')}</h4>

          <div className="space-y-2">
            <Label htmlFor="getExpandableContent">
              Expandable Content Function
            </Label>
            <Input
              id="getExpandableContent"
              value={
                (localProps.getExpandableContent as string) ||
                "getExpandableContent"
              }
              onChange={(e) =>
                handlePropsChange("getExpandableContent", e.target.value)
              }
              placeholder="getExpandableContent"
            />
            <div className="text-xs text-muted-foreground">
              Function name that returns expandable content for each row
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="showExpandIndicators">
                Show Expand Indicators
              </Label>
              <div className="text-xs text-muted-foreground">
                Show expand/collapse chevron icons
              </div>
            </div>
            <Switch
              id="showExpandIndicators"
              checked={(localProps.showExpandIndicators as boolean) !== false}
              onCheckedChange={(checked) =>
                handlePropsChange("showExpandIndicators", checked)
              }
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="allowMultipleExpanded">
                Allow Multiple Expanded
              </Label>
              <div className="text-xs text-muted-foreground">
                Allow multiple rows to be expanded simultaneously
              </div>
            </div>
            <Switch
              id="allowMultipleExpanded"
              checked={(localProps.allowMultipleExpanded as boolean) !== false}
              onCheckedChange={(checked) =>
                handlePropsChange("allowMultipleExpanded", checked)
              }
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="expandIcon">{t('platform.layout.element-layout.expand_icon')}</Label>
            <Input
              id="expandIcon"
              value={(localProps.expandIcon as string) || "fa-chevron-right"}
              onChange={(e) => handlePropsChange("expandIcon", e.target.value)}
              placeholder="fa-chevron-right"
            />
            <div className="text-xs text-muted-foreground">
              FontAwesome icon class for expand indicator
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="collapseIcon">{t('platform.layout.element-layout.collapse_icon')}</Label>
            <Input
              id="collapseIcon"
              value={(localProps.collapseIcon as string) || "fa-chevron-down"}
              onChange={(e) =>
                handlePropsChange("collapseIcon", e.target.value)
              }
              placeholder="fa-chevron-down"
            />
            <div className="text-xs text-muted-foreground">
              FontAwesome icon class for collapse indicator
            </div>
          </div>
        </div>

        {/* Add New Button Settings */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="showAddNew">Show "Add New" Button</Label>
              <div className="text-xs text-muted-foreground">
                Enable the "Add New" button in the table header
              </div>
            </div>
            <Switch
              id="showAddNew"
              checked={(localProps.showAddNew as boolean) || false}
              onCheckedChange={(checked) => {
                handlePropsChange("showAddNew", checked);
                // Automatically add onAddNew property when enabling the button
                if (checked && !localProps.onAddNew) {
                  handlePropsChange("onAddNew", "handleAddNew");
                }
              }}
            />
          </div>

          {(localProps.showAddNew as boolean) && (
            <div className="space-y-2">
              <Label htmlFor="addNewButtonText">Button Text</Label>
              <Input
                id="addNewButtonText"
                value={(localProps.addNewButtonText as string) || ""}
                onChange={(e) =>
                  handlePropsChange("addNewButtonText", e.target.value)
                }
                placeholder={`Add New ${
                  currentDataSource
                    ? DATA_SOURCES[currentDataSource]?.title
                    : "Item"
                }`}
              />
              <div className="text-xs text-muted-foreground">
                {`Leave empty to use default text: "Add New ${
                  currentDataSource
                    ? DATA_SOURCES[currentDataSource]?.title
                    : "Item"
                }"`}
              </div>

              <Label htmlFor="onAddNew">Handler Function</Label>
              <Input
                id="onAddNew"
                value={(localProps.onAddNew as string) || "handleAddNew"}
                onChange={(e) => handlePropsChange("onAddNew", e.target.value)}
                placeholder="handleAddNew"
              />
              <div className="text-xs text-muted-foreground">
                Function name that will be called when the button is clicked
              </div>
            </div>
          )}
        </div>

        {/* Action Button Handlers */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium">Action Handlers</h4>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="onEdit">Edit Handler</Label>
              <Input
                id="onEdit"
                value={(localProps.onEdit as string) || ""}
                onChange={(e) => handlePropsChange("onEdit", e.target.value)}
                placeholder="handleEdit"
              />
              <div className="text-xs text-muted-foreground">
                Function name for edit action (optional)
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="onDuplicate">Duplicate Handler</Label>
              <Input
                id="onDuplicate"
                value={(localProps.onDuplicate as string) || ""}
                onChange={(e) =>
                  handlePropsChange("onDuplicate", e.target.value)
                }
                placeholder="handleDuplicate"
              />
              <div className="text-xs text-muted-foreground">
                Function name for duplicate action (optional)
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="onDelete">Delete Handler</Label>
              <Input
                id="onDelete"
                value={(localProps.onDelete as string) || ""}
                onChange={(e) => handlePropsChange("onDelete", e.target.value)}
                placeholder="handleDelete"
              />
              <div className="text-xs text-muted-foreground">
                Function name for delete action (optional)
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderTableWidgetProps = () => {
    const currentDataSource = localProps.dataSource as DataSourceType;
    const selectedColumns = Array.isArray(localProps.selectedColumns)
      ? (localProps.selectedColumns as string[])
      : [];
    const columnLabels =
      (localProps.columnLabels as Record<string, string>) || {};

    return (
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="tableTitle">Title</Label>
          <Input
            id="tableTitle"
            value={(localProps.title as string) || "Table"}
            onChange={(e) => handlePropsChange("title", e.target.value)}
            placeholder={t('platform.layout.element-layout.enter_table_title_placeholder')}
          />
        </div>

        <DataSourceOrganizer
          dataSource={currentDataSource}
          selectedColumns={selectedColumns}
          columnLabels={columnLabels}
          onDataSourceChange={(dataSource) =>
            handlePropsChange("dataSource", dataSource)
          }
          onSelectedColumnsChange={(columns) =>
            handlePropsChange("selectedColumns", columns)
          }
          onColumnLabelsChange={(labels) =>
           // handlePropsChange("columnLabels", labels)
           console.log('labels:', labels)
          }
        />

        {/* Add New Button Settings */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="showAddNew">{t('platform.layout.element-layout.show_add_new_button_label')}</Label>
              <div className="text-xs text-muted-foreground">
                Enable the "Add New" button in the table header
              </div>
            </div>
            <Switch
              id="showAddNew"
              checked={(localProps.showAddNew as boolean) || false}
              onCheckedChange={(checked) => {
                handlePropsChange("showAddNew", checked);
                // Automatically add onAddNew property when enabling the button
                if (checked && !localProps.onAddNew) {
                  handlePropsChange("onAddNew", "handleAddNew");
                }
              }}
            />
          </div>

          {(localProps.showAddNew as boolean) && (
            <div className="space-y-2">
              <Label htmlFor="addNewButtonText">{t('platform.layout.element-layout.button_text_label')}</Label>
              <Input
                id="addNewButtonText"
                value={(localProps.addNewButtonText as string) || ""}
                onChange={(e) =>
                  handlePropsChange("addNewButtonText", e.target.value)
                }
                placeholder={`Add New ${
                  currentDataSource
                    ? DATA_SOURCES[currentDataSource]?.title
                    : "Item"
                }`}
              />
              <div className="text-xs text-muted-foreground">
                {`Leave empty to use default text: "Add New ${
                  currentDataSource
                    ? DATA_SOURCES[currentDataSource]?.title
                    : "Item"
                }"`}
              </div>

              <Label htmlFor="onAddNew">{t('platform.layout.element-layout.handler_function_label')}</Label>
              <Input
                id="onAddNew"
                value={(localProps.onAddNew as string) || "handleAddNew"}
                onChange={(e) => handlePropsChange("onAddNew", e.target.value)}
                placeholder="handleAddNew"
              />
              <div className="text-xs text-muted-foreground">
                Function name that will be called when the button is clicked
              </div>
            </div>
          )}
        </div>

        {/* Action Button Handlers */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium">{t('platform.layout.element-layout.action_handlers_2')}</h4>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="onEdit">{t('platform.layout.element-layout.edit_handler_label')}</Label>
              <Input
                id="onEdit"
                value={(localProps.onEdit as string) || ""}
                onChange={(e) => handlePropsChange("onEdit", e.target.value)}
                placeholder="handleEdit"
              />
              <div className="text-xs text-muted-foreground">
                Function name for edit action (optional)
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="onDuplicate">{t('platform.layout.element-layout.duplicate_handler_label')}</Label>
              <Input
                id="onDuplicate"
                value={(localProps.onDuplicate as string) || ""}
                onChange={(e) =>
                  handlePropsChange("onDuplicate", e.target.value)
                }
                placeholder="handleDuplicate"
              />
              <div className="text-xs text-muted-foreground">
                Function name for duplicate action (optional)
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="onDelete">{t('platform.layout.element-layout.delete_handler_label')}</Label>
              <Input
                id="onDelete"
                value={(localProps.onDelete as string) || ""}
                onChange={(e) => handlePropsChange("onDelete", e.target.value)}
                placeholder="handleDelete"
              />
              <div className="text-xs text-muted-foreground">
                Function name for delete action (optional)
              </div>
            </div>
          </div>
        </div>

        {/* Legacy Column Config Support */}
        {Array.isArray(localProps.columnsConfig) &&
          localProps.columnsConfig.length > 0 && (
            <div className="space-y-2">
              <Label>{t('platform.layout.element-layout.legacy_column_configuration')}</Label>
              <div className="text-xs text-muted-foreground mb-2">
                This is using the legacy column configuration. Consider using
                the column selector above.
              </div>
              {(localProps.columnsConfig || []).map(
                (
                  col: { key: string; label: string; field: string },
                  idx: number
                ) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <Input
                      value={col.label}
                      onChange={(e) => {
                        const newConfig = Array.isArray(
                          localProps.columnsConfig
                        )
                          ? [...localProps.columnsConfig]
                          : [];
                        newConfig[idx] = { ...col, label: e.target.value };
                        handlePropsChange("columnsConfig", newConfig);
                      }}
                      placeholder={t('platform.layout.element-layout.column_label')}
                      className="w-32"
                    />
                    <Select
                      value={col.field}
                      onValueChange={(value) => {
                        const newConfig = Array.isArray(
                          localProps.columnsConfig
                        )
                          ? [...localProps.columnsConfig]
                          : [];
                        newConfig[idx] = { ...col, field: value };
                        handlePropsChange("columnsConfig", newConfig);
                      }}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue placeholder={t('platform.layout.element-layout.select_field')} />
                      </SelectTrigger>
                      <SelectContent>
                        {currentDataSource &&
                          DATA_SOURCES[currentDataSource]?.availableFields.map(
                            (field) => (
                              <SelectItem key={field} value={field}>
                                {field}
                              </SelectItem>
                            )
                          )}
                      </SelectContent>
                    </Select>
                    <button
                      type="button"
                      className="text-xs text-red-500 hover:underline"
                      onClick={() => {
                        const newConfig = Array.isArray(
                          localProps.columnsConfig
                        )
                          ? [...localProps.columnsConfig]
                          : [];
                        newConfig.splice(idx, 1);
                        handlePropsChange("columnsConfig", newConfig);
                      }}
                    >
                      Remove
                    </button>
                  </div>
                )
              )}
            </div>
          )}
      </div>
    );
  };

  const renderGridOrTableWidgetProps = () => (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="requiredTags">{t('platform.layout.element-layout.required_tags_comma_separated')}</Label>
        <Input
          id="requiredTags"
          value={
            Array.isArray(localProps.requiredTags)
              ? localProps.requiredTags.join(", ")
              : ""
          }
          onChange={(e) => {
            const tags = e.target.value
              .split(",")
              .map((tag) => tag.trim())
              .filter(Boolean);
            handlePropsChange("requiredTags", tags);
          }}
          placeholder={t('platform.layout.element-layout.eg_location_character')}
        />
        <span className="text-xs text-muted-foreground">
          Only show items with at least one of these tags.
        </span>
      </div>
    </div>
  );

  const renderTMSTableWidgetProps = () => (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="tmsTableTitle">{t('platform.common.title')}</Label>
        <Input
          id="tmsTableTitle"
          value={(localProps.title as string) || "TMS Table"}
          onChange={(e) => handlePropsChange("title", e.target.value)}
          placeholder={t('platform.layout.element-layout.enter_tms_table_title')}
        />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="showTitle">{t('platform.layout.element-layout.show_title')}</Label>
        <div className="flex items-center space-x-2">
          <Switch
            id="showTitle"
            checked={(localProps.showTitle as boolean) ?? true}
            onCheckedChange={(checked) =>
              handlePropsChange("showTitle", checked)
            }
          />
          <Label htmlFor="showTitle">{t('platform.layout.element-layout.display_widget_title')}</Label>
        </div>
      </div>
    </div>
  );

  const renderWidgetSpecificProps = () => {
    switch (localType) {
      case "title-widget":
        return renderTitleWidgetProps();
      case "stats-widget":
        return renderStatsWidgetProps();
      case "list-widget":
        return renderListWidgetProps();
      case "table-widget":
        return (
          <>
            {renderTableWidgetProps()}
            {renderGridOrTableWidgetProps()}
          </>
        );
      case "expandable-table-widget":
        return (
          <>
            {renderExpandableTableWidgetProps()}
            {renderGridOrTableWidgetProps()}
          </>
        );
      case "grid-widget":
        return renderGridOrTableWidgetProps();
      case "tms-table-widget":
        return renderTMSTableWidgetProps();
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Element Type */}
      <div className="space-y-2">
        <Label htmlFor="elementType">{t('platform.layout.element-layout.element_type')}</Label>
        <Select value={localType} onValueChange={handleTypeChange}>
          <SelectTrigger>
            <SelectValue placeholder={t('platform.layout.element-layout.select_element_type')} />
          </SelectTrigger>
          <SelectContent>
            {availableTypes.map((type) => (
              <SelectItem key={type} value={type}>
                {type
                  .replace("-", " ")
                  .replace(/\b\w/g, (l) => l.toUpperCase())}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Separator />

      {/* Common Properties */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium">{t('platform.layout.element-layout.common_properties')}</h3>
        {renderCommonProps()}
      </div>

      {/* Widget-Specific Properties */}
      {renderWidgetSpecificProps() && (
        <>
          <Separator />
          <div className="space-y-4">
            <h3 className="text-sm font-medium">{t('platform.layout.element-layout.widget_properties')}</h3>
            {renderWidgetSpecificProps()}
          </div>
        </>
      )}

      <Separator />

      {/* Actions */}
      <div className="space-y-3">
        <div className="flex gap-2">
          <Button onClick={handleSave} disabled={!isDirty} className="flex-1">
            Save Changes
          </Button>
          <Button
            onClick={handleReset}
            disabled={!isDirty}
            variant="outline"
            className="flex-1"
          >
            Reset
          </Button>
        </div>

        <Button onClick={handleRemove} variant="destructive" className="w-full">
          Remove Element
        </Button>
      </div>
    </div>
  );
}
